import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { AvatarModule } from 'primeng/avatar';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { PartnerService } from '../../services/partner';
import { PartnerPremiumAccessService } from '../../services/partner-premium-access';
import { PartnerMessageService } from '../../services/partner-message';
import { GoLiveService, GoLiveStatus, PartnerGatewayRow } from '../../services/go-live';
import { ToastService } from '../../services/toast';
// The Messaging tab edits MessageRule directly. The old channel/type checkboxes wrote BIT
// columns that nothing reads any more, so they would have looked like they worked.
import { MessagingRulesTab } from '../messaging/messaging-rules';

/**
 * One page per partner, mirroring the client detail page: everything about a partner is reached
 * from here instead of from three separate screens. Tabs are the same idea as the client's
 * (Payment Config / Live & Test), so an admin who knows one knows the other.
 */
@Component({
  selector: 'app-partner-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, TabsModule, TagModule, ChipModule, AvatarModule, DialogModule, SelectModule, MessagingRulesTab],
  templateUrl: './partner-detail.html',
  styleUrls: ['./partner-detail.css'],
})
export class PartnerDetail implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private svc = inject(PartnerService);
  private goLive = inject(GoLiveService);
  private access = inject(PartnerPremiumAccessService);
  // Retained only for the legacy loadMessaging/saveMessaging helpers still referenced
  // elsewhere in this component; the Messaging tab itself now edits MessageRule directly.
  private messaging = inject(PartnerMessageService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly partnerId = signal<number | null>(null);
  readonly partner = signal<any | null>(null);
  readonly loading = signal(false);
  readonly activeTab = signal<'gateway' | 'products' | 'messaging' | 'mode'>('gateway');

  // ---- gateway ----
  readonly gateway = signal<PartnerGatewayRow | null>(null);
  readonly options = signal<{ value: number; label: string }[]>([]);
  readonly pick = signal<number | null>(null);
  readonly savingGw = signal(false);

  // ---- products (premium plans this partner may sell) ----
  readonly allRules = signal<any[]>([]);
  readonly assigned = signal<any[]>([]);
  readonly productsLoading = signal(false);
  readonly savingProduct = signal(false);
  /** Ids this partner already has, for a quick membership test in the template. */
  readonly assignedIds = computed(() => new Set(this.assigned().map((a) => a.premiumID ?? a.premiumId)));
  /** Only the plans NOT yet given to this partner — the "add" list. */
  readonly available = computed(() => this.allRules().filter((r) => !this.assignedIds().has(r.id)));
  readonly addPick = signal<number | null>(null);

  // ---- messaging channels ----
  readonly msgWhatsApp = signal(false);
  readonly msgSms = signal(false);
  readonly msgEmail = signal(false);
  readonly savingMsg = signal(false);
  /** Which documents/events are sent. Kept alongside the channels so saving the tab cannot
   *  silently switch a partner's notifications off — SaveMessageRequest carries all of them. */
  readonly msgTypes = signal<Record<string, boolean>>({
    isOtp: true, isPaymentLink: true, isPaymentReceipt: true,
    isCoi: true, isPolicyIssued: true, isKyc: true,
  });
  readonly msgTypeList = [
    { key: 'isOtp', label: 'OTP', hint: 'Login and verification codes.' },
    { key: 'isPaymentLink', label: 'Payment link', hint: 'The link a member pays through.' },
    { key: 'isPaymentReceipt', label: 'Payment receipt', hint: 'Sent once a payment succeeds.' },
    { key: 'isCoi', label: 'Certificate (COI)', hint: 'The insurance certificate.' },
    { key: 'isPolicyIssued', label: 'Policy issued', hint: 'Confirmation that cover has started.' },
    { key: 'isKyc', label: 'KYC', hint: 'KYC status updates.' },
  ];

  // ---- live / test ----
  readonly status = signal<GoLiveStatus | null>(null);
  readonly checking = signal(false);
  readonly savingMode = signal(false);

  readonly mode = computed<'LIVE' | 'TEST'>(() => this.gateway()?.mode ?? this.status()?.mode ?? 'TEST');

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/partners']);
      return;
    }
    this.partnerId.set(id);
    this.load();
  }

  load() {
    const id = this.partnerId();
    if (!id) return;
    this.loading.set(true);
    this.svc.getById(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.partner.set(res?.data ?? null);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Could not load the partner.');
      },
    });
    this.loadGateway();
    this.loadStatus();
    this.loadProducts();
    this.loadMessaging();
  }

  initials(name?: string | null): string {
    const n = (name || '').trim();
    if (!n) return 'P';
    const parts = n.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'P';
  }

  back() {
    this.router.navigate(['/partners']);
  }

  onTabChange(v: any) {
    this.activeTab.set(v);
  }

  // ===================== gateway =====================
  private loadGateway() {
    const id = this.partnerId();
    if (!id) return;
    this.goLive.partnerGateways().subscribe({
      next: (res: any) => {
        const row = ((res?.data ?? []) as PartnerGatewayRow[]).find((r) => r.partnerId === id) ?? null;
        this.gateway.set(row);
        this.pick.set(row?.integrationId ?? null);
      },
      error: () => this.gateway.set(null),
    });
    this.goLive.gatewayOptions(id).subscribe({
      next: (res: any) => this.options.set(res?.data ?? []),
      error: () => this.options.set([]),
    });
  }

  onPick(v: string) {
    this.pick.set(v ? Number(v) : null);
  }

  saveGateway() {
    const id = this.partnerId();
    if (!id) return;
    this.savingGw.set(true);
    this.goLive.assignGateway(id, this.pick()).subscribe({
      next: (res: any) => {
        this.savingGw.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not assign the gateway.');
          return;
        }
        this.toast.success(res?.message || 'Gateway assigned.');
        this.loadGateway();
      },
      error: () => {
        this.savingGw.set(false);
        this.toast.error('Could not assign the gateway.');
      },
    });
  }

  // ===================== products =====================
  /**
   * Only this partner's plans are listed — the standalone Premium Access page shows every
   * partner's mappings at once, which is the wrong question when you are looking at one partner.
   */
  private loadProducts() {
    const id = this.partnerId();
    if (!id) return;
    this.productsLoading.set(true);
    this.access.getByPartner(id).subscribe({
      next: (res: any) => {
        this.productsLoading.set(false);
        this.assigned.set(res?.data ?? []);
      },
      error: () => {
        this.productsLoading.set(false);
        this.assigned.set([]);
      },
    });
    // Assignable, not getAll(): a CLIENT-owned master policy is not a partner's to sell, and
    // listing it invites assigning cover this partner has no route to market.
    this.access.assignable(id).subscribe({
      next: (res: any) => this.allRules.set(res?.data ?? []),
      error: () => this.allRules.set([]),
    });
  }

  /** Gross (GST-inclusive) is what the customer pays and the only figure an admin recognises. */
  ruleLabel(r: any): string {
    const gross = r?.grossPremium ?? r?.basePremium;
    const price = gross != null ? ' — Rs ' + Math.round(Number(gross)) : '';
    const name = r?.masterPolicyName || r?.premiumCode || r?.coverageType || 'Plan #' + r?.id;
    return `${name}${price}`;
  }

  /** p-select needs {label,value} pairs. */
  readonly planOptions = computed(() =>
    this.available().map((r) => ({ label: this.ruleLabel(r), value: r.id })));

  addProduct() {
    const id = this.partnerId();
    const premiumId = this.addPick();
    if (!id || !premiumId) return;
    this.savingProduct.set(true);
    this.access.assign({ partnerID: id, premiumIds: [premiumId], isEnable: true } as any).subscribe({
      next: (res: any) => {
        this.savingProduct.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not assign the plan.');
          return;
        }
        this.toast.success('Plan assigned.');
        this.addPick.set(null);
        this.loadProducts();
    this.loadMessaging();
        // A newly sellable plan can flip the go-live checklist, so re-run it.
        this.loadStatus();
      },
      error: () => {
        this.savingProduct.set(false);
        this.toast.error('Could not assign the plan.');
      },
    });
  }

  removeProduct(row: any) {
    this.savingProduct.set(true);
    this.access.delete(row.id).subscribe({
      next: (res: any) => {
        this.savingProduct.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not remove the plan.');
          return;
        }
        this.toast.success('Plan removed.');
        this.loadProducts();
    this.loadMessaging();
        this.loadStatus();
      },
      error: () => {
        this.savingProduct.set(false);
        this.toast.error('Could not remove the plan.');
      },
    });
  }

  // ===================== messaging =====================
  /** This partner's delivery channels only — the standalone page grids every partner at once. */
  private loadMessaging() {
    const id = this.partnerId();
    if (!id) return;
    this.messaging.getByPartner(id).subscribe({
      next: (res: any) => {
        const r = res?.data ?? {};
        this.msgWhatsApp.set(!!r.isWhatsApp);
        this.msgSms.set(!!r.isSMS);
        this.msgEmail.set(!!r.isEmail);
        // A partner with no row yet has every message type on by default; an existing row wins.
        const has = r.messageTypeId != null;
        this.msgTypes.set({
          isOtp: has ? !!r.isOtp : true,
          isPaymentLink: has ? !!r.isPaymentLink : true,
          isPaymentReceipt: has ? !!r.isPaymentReceipt : true,
          isCoi: has ? !!r.isCoi : true,
          isPolicyIssued: has ? !!r.isPolicyIssued : true,
          isKyc: has ? !!r.isKyc : true,
        });
      },
      error: () => {
        // No channel row yet is normal for a new partner — treat as all-off.
        this.msgWhatsApp.set(false);
        this.msgSms.set(false);
        this.msgEmail.set(false);
      },
    });
  }

  toggleMsgType(key: string, on: boolean) {
    this.msgTypes.set({ ...this.msgTypes(), [key]: on });
  }

  saveMessaging() {
    const id = this.partnerId();
    if (!id) return;
    this.savingMsg.set(true);
    this.messaging
      .save({
        partnerId: id,
        isWhatsApp: this.msgWhatsApp(),
        isSMS: this.msgSms(),
        isEmail: this.msgEmail(),
        ...this.msgTypes(),
      } as any)
      .subscribe({
        next: (res: any) => {
          this.savingMsg.set(false);
          if (res?.success === false) {
            this.toast.error(res?.message || 'Could not save the channels.');
            return;
          }
          this.toast.success('Delivery channels saved.');
          this.loadMessaging();
        },
        error: () => {
          this.savingMsg.set(false);
          this.toast.error('Could not save the channels.');
        },
      });
  }

  // ===================== live / test =====================
  private loadStatus() {
    const id = this.partnerId();
    if (!id) return;
    this.checking.set(true);
    this.goLive.status('PARTNER', id).subscribe({
      next: (res: any) => {
        this.checking.set(false);
        this.status.set(res?.data ?? null);
      },
      error: () => {
        this.checking.set(false);
        this.status.set(null);
      },
    });
  }

  setMode(mode: 'LIVE' | 'TEST') {
    const id = this.partnerId();
    if (!id) return;
    this.savingMode.set(true);
    this.goLive.setMode('PARTNER', id, mode).subscribe({
      next: (res: any) => {
        this.savingMode.set(false);
        if (res?.success === false) {
          // The server re-runs the checks, so this fires when something changed since the page
          // loaded. Show the refreshed list rather than a bare failure.
          this.toast.error(res?.message || 'Could not change the mode.');
          if (res?.data?.checks && this.status()) {
            this.status.set({ ...this.status()!, checks: res.data.checks, canGoLive: false });
          }
          return;
        }
        this.toast.success(res?.message || 'Mode updated.');
        this.loadStatus();
        this.loadGateway();
      },
      error: () => {
        this.savingMode.set(false);
        this.toast.error('Could not change the mode.');
      },
    });
  }
}
